import { useNavigate } from 'react-router';
import { CheckCircle2, ChevronLeft, Download, Share } from 'lucide-react';

import { Button, Card } from '@llura/ui';

import { useStrings } from '../../hooks/useStrings';
import { useHaptic } from '../../hooks/useHaptic';
import { getInstallPlatform, isStandalone, useInstallPrompt } from '../../lib/platform';

/**
 * OS-aware "Add to Home Screen" tutorial at `/profile/install`. Detects iOS
 * Safari / Android Chrome / desktop and shows the matching steps. When the
 * browser exposed a native install prompt (Chrome/Edge), a one-tap install
 * button is offered on top of the manual steps.
 */
export function InstallGuidePage() {
  const navigate = useNavigate();
  const haptic = useHaptic();
  const { strings } = useStrings();
  const t = strings.notifications.install;

  const platform = getInstallPlatform();
  const installed = isStandalone();
  const { available, promptInstall } = useInstallPrompt();
  const guide = t.platforms[platform];

  const onInstall = async () => {
    haptic.tap();
    await promptInstall();
  };

  return (
    <div className="mx-auto w-full max-w-md space-y-5 px-4 py-5">
      <header className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          aria-label={t.backAria}
          className="rounded-full"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-serif text-2xl leading-tight">{t.title}</h1>
          <p className="text-xs text-muted-foreground">{t.intro}</p>
        </div>
      </header>

      {installed ? (
        <Card className="flex items-start gap-3 border-primary/40 bg-card p-5">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="space-y-1">
            <h2 className="font-serif text-lg leading-tight">{t.installedTitle}</h2>
            <p className="text-sm text-muted-foreground">{t.installedBody}</p>
          </div>
        </Card>
      ) : (
        <Card className="space-y-4 border-border bg-card p-5">
          <div className="flex items-center gap-2">
            {platform === 'ios' ? (
              <Share className="h-4 w-4 text-primary" />
            ) : (
              <Download className="h-4 w-4 text-primary" />
            )}
            <h2 className="font-serif text-lg leading-tight">{guide.heading}</h2>
          </div>

          {available && (
            <Button onClick={onInstall} className="w-full gap-2 rounded-xl">
              <Download className="h-4 w-4" />
              {t.oneTapCta}
            </Button>
          )}

          <ol className="space-y-3">
            {guide.steps.map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-xs text-primary">
                  {i + 1}
                </span>
                <span className="pt-0.5 text-foreground/90">{step}</span>
              </li>
            ))}
          </ol>
        </Card>
      )}
    </div>
  );
}
